/**
 * Platform-aware Amplitude analytics.
 * - Web: @amplitude/analytics-browser with transport: 'beacon' to avoid CORS.
 * - iOS/Android: @amplitude/analytics-react-native.
 * API key: EXPO_PUBLIC_AMPLITUDE_API_KEY (web) or extra.amplitudeApiKey (native).
 */

import Constants from 'expo-constants';
import { Platform } from 'react-native';

let cachedApiKey: string | undefined;

function getApiKey(): string {
  if (cachedApiKey !== undefined) return cachedApiKey;
  const isWeb = Platform.OS === 'web';
  cachedApiKey =
    isWeb
      ? (process.env.EXPO_PUBLIC_AMPLITUDE_API_KEY ?? '')
      : (Constants.expoConfig?.extra as { amplitudeApiKey?: string } | undefined)?.amplitudeApiKey ?? '';
  return cachedApiKey;
}

let initialized = false;

function getRnSdk(): typeof import('@amplitude/analytics-react-native') | null {
  if (!getApiKey()) return null;
  try {
    return require('@amplitude/analytics-react-native');
  } catch {
    return null;
  }
}

function getBrowserSdk(): typeof import('@amplitude/analytics-browser') | null {
  if (!getApiKey()) return null;
  try {
    return require('@amplitude/analytics-browser');
  } catch {
    return null;
  }
}

const isWeb = Platform.OS === 'web';

export function init(): void {
  const apiKey = getApiKey();
  if (!apiKey) {
    if (__DEV__) console.log('[Analytics] No API key; analytics disabled');
    initialized = true;
    return;
  }

  if (isWeb) {
    const browser = getBrowserSdk();
    if (!browser) {
      if (__DEV__) console.warn('[Analytics] Browser SDK not available');
      initialized = true;
      return;
    }
    try {
      browser.init(apiKey, {
        transport: 'beacon',
        remoteConfig: { fetchRemoteConfig: false },
      });
      initialized = true;
      if (__DEV__) console.log('[Analytics] Initialized (browser, beacon)');
    } catch (err) {
      if (__DEV__) console.warn('[Analytics] Browser init failed:', err);
      initialized = true;
    }
    return;
  }

  const rn = getRnSdk();
  if (!rn) {
    if (__DEV__) console.warn('[Analytics] React Native SDK not available (e.g. Expo Go)');
    initialized = true;
    return;
  }
  try {
    rn.init(apiKey);
    initialized = true;
    if (__DEV__) console.log('[Analytics] Initialized (react-native)');
  } catch (err) {
    if (__DEV__) console.warn('[Analytics] React Native init failed:', err);
    initialized = true;
  }
}

/**
 * Track an event. Routes to the correct SDK based on platform.
 * Platform is set at the user level via setUserProperties, not per-event.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (!initialized || !getApiKey()) return;
  try {
    if (isWeb) {
      const browser = getBrowserSdk();
      if (browser) browser.track(event, properties);
    } else {
      const rn = getRnSdk();
      if (rn) rn.track(event, properties);
    }
  } catch {
    // no-op
  }
}

export function setUserId(userId: string | null): void {
  if (!initialized || !getApiKey()) return;
  try {
    if (isWeb) {
      const browser = getBrowserSdk();
      if (browser) browser.setUserId(userId ?? undefined);
    } else {
      const rn = getRnSdk();
      if (rn) rn.setUserId(userId ?? undefined);
    }
  } catch {
    // no-op
  }
}

/**
 * Set user properties (e.g. email, name) and platform so Amplitude shows them correctly.
 * Call after setUserId when the user is known. Platform is set automatically from Platform.OS.
 */
export function setUserProperties(properties: { email?: string | null; name?: string | null }): void {
  if (!initialized || !getApiKey()) return;
  try {
    const { Identify } = isWeb ? require('@amplitude/analytics-browser') : require('@amplitude/analytics-react-native');
    const identify = new Identify();
    identify.set('platform', Platform.OS);
    if (properties.email != null && properties.email !== '') identify.set('email', properties.email);
    if (properties.name != null && properties.name !== '') identify.set('name', properties.name);

    if (isWeb) {
      const browser = getBrowserSdk();
      if (browser) browser.identify(identify);
    } else {
      const rn = getRnSdk();
      if (rn) rn.identify(identify);
    }
  } catch {
    // no-op
  }
}

export function reset(): void {
  if (!initialized || !getApiKey()) return;
  try {
    if (isWeb) {
      const browser = getBrowserSdk();
      if (browser) browser.reset();
    } else {
      const rn = getRnSdk();
      if (rn) rn.reset();
    }
  } catch {
    // no-op
  }
}
